import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type {
  SellerProductReviewStatus,
  SellerProductReviewView,
} from "@/lib/api/types";
import {
  ReviewStars,
  ReviewActionsMenu,
} from "@/modules/reviews/components/review-ui";
import {
  formatReviewDate,
  reviewAvatar,
  reviewColorIndex,
  reviewInitials,
  reviewStatusTone,
} from "@/modules/reviews/lib/review-display";

type ReviewCardProps = {
  locale: Locale;
  review: SellerProductReviewView;
  isMutating: boolean;
  onToggleStatus: (
    id: string,
    nextStatus: SellerProductReviewStatus,
  ) => Promise<void>;
};

export function ReviewCard({
  locale,
  review,
  isMutating,
  onToggleStatus,
}: ReviewCardProps) {
  const t = useTranslations();
  const productName =
    locale === "ar"
      ? review.productNameAr
      : (review.productNameEn ?? review.productNameAr);
  const avatar = reviewAvatar(reviewColorIndex(review.id));
  const tone = reviewStatusTone(review.status, {
    published: t(keys.reviews.statusPublished),
    hidden: t(keys.reviews.statusHidden),
  });
  return (
    <div className="flex flex-col rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--card)] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-[3px] hover:border-[color:var(--border-strong)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.10)]">
      <div className="px-[18px] pb-[14px] pt-4">
        <div className="mb-[13px] flex items-start gap-[11px]">
          {/* Decorative monogram — the buyer's name is rendered right beside it,
              so this is redundant for a screen reader (and its tint-on-tint
              colours are below AA as text). */}
          <div
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-extrabold"
            style={{ background: avatar.bg, color: avatar.fg }}
          >
            {reviewInitials(review.buyerName)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-bold text-[color:var(--text-primary)]">
              {review.buyerName}
            </div>
            <div className="mt-[3px] flex items-center gap-[7px]">
              <ReviewStars value={review.rating} size={14} />
              <span className="text-[11.5px] font-medium text-[color:var(--text-muted)]">
                {formatReviewDate(review.createdAt, locale)}
              </span>
            </div>
          </div>
          <span
            className="inline-flex shrink-0 items-center gap-[5px] rounded-full px-[9px] py-[3px] text-[11px] font-semibold"
            style={{ background: tone.bg, color: tone.fg }}
          >
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] rounded-full"
              style={{ background: tone.dot }}
            />
            {tone.label}
          </span>
        </div>
        {/* dir=auto: the body is buyer-written text of unknown direction. In the
            RTL portal an English review had its trailing "." reordered to the
            front (".Great product…"). Let the browser detect per-string. */}
        <p
          dir="auto"
          className="m-0 text-[13px] leading-[1.6] text-[color:var(--text-secondary)]"
        >
          {review.body ?? t(keys.reviews.noBody)}
        </p>
      </div>

      <div className="mt-auto flex items-center justify-between gap-[10px] rounded-b-[18px] border-t border-[color:var(--hair)] bg-[color:var(--soft)] px-[18px] py-3">
        <span className="inline-flex min-w-0 items-center gap-[7px]">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border border-[color:var(--border-light)] bg-[color:var(--card)] text-[color:var(--text-muted)]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            </svg>
          </span>
          <span className="truncate text-[12px] font-semibold text-[color:var(--text-muted)]">
            {productName}
          </span>
        </span>
        <div className="flex shrink-0 items-center gap-[7px]">
          <ReviewActionsMenu
            status={review.status}
            isMutating={isMutating}
            onToggleStatus={(nextStatus) =>
              void onToggleStatus(review.id, nextStatus)
            }
          />
        </div>
      </div>
    </div>
  );
}
